import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { 
  Phone, 
  Mail, 
  Building2, 
  MapPin, 
  Globe, 
  Linkedin, 
  Twitter, 
  Facebook, 
  Instagram,
  Download,
  Share2
} from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { generateVCardString, measureVCardPayload, QR_ERROR_CORRECTION_LEVEL } from '../lib/vcard';

export const VCardPreview = ({ vcard, showActions = true, className, publicDownloadUrl }) => {
  const fallbackVCardString = generateVCardString(vcard);
  const qrPayload = vcard.vcard_qr_payload || vcard.vcard_string || fallbackVCardString;
  const vcfPayload = vcard.vcard_vcf_payload || vcard.vcard_string || qrPayload;
  const payloadMetrics = measureVCardPayload(qrPayload);
  const photoStatus = vcard.offline_photo_status || {};
  const qrModuleCount = payloadMetrics.estimated_qr_version ? 17 + (4 * payloadMetrics.estimated_qr_version) : 177;
  const qrSize = Math.min(384, Math.max(160, qrModuleCount * 4));

  const handleDownloadVCard = () => {
    const blob = new Blob([vcfPayload], { type: 'text/vcard;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${vcard.full_name?.replace(/\s+/g, '_') || 'contact'}.vcf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadQR = () => {
    const svg = document.getElementById(`qr-${vcard.id}`);
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      
      const pngUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = pngUrl;
      link.download = `qr-${vcard.full_name?.replace(/\s+/g, '_') || 'contact'}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/card/${vcard.id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Tarjeta de ${vcard.full_name}`,
          text: `Mira la tarjeta digital de ${vcard.full_name}`,
          url: shareUrl,
        });
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Share cancelled');
        }
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert('Enlace copiado al portapapeles');
    }
  };

  const social = vcard.social_links || {};

  return (
    <div 
      className={cn("bg-white shadow-xl border-t-4 border-[#C5C51E] overflow-hidden max-w-md mx-auto", className)}
      data-testid="vcard-preview"
    >
      {/* Header with photo */}
      <div className="bg-[#3C3C3C] p-6 text-center">
        {vcard.photo_url ? (
          <img 
            src={vcard.photo_url} 
            alt={vcard.full_name}
            className="w-24 h-24 rounded-full mx-auto mb-4 object-cover border-4 border-white"
          />
        ) : (
          <div className="w-24 h-24 rounded-full mx-auto mb-4 bg-[#5E5E5E] flex items-center justify-center border-4 border-white">
            <span className="text-3xl font-semibold text-white">
              {vcard.full_name?.charAt(0).toUpperCase()}
            </span>
          </div>
        )}
        <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
          {vcard.full_name || 'Nombre'}
        </h2>
        {vcard.job_title && (
          <p className="text-[#A2A2A2] mt-1">{vcard.job_title}</p>
        )}
        {vcard.company && (
          <p className="text-[#C5C51E] font-medium mt-1">{vcard.company}</p>
        )}
      </div>

      {/* Contact info */}
      <div className="p-6 space-y-4">
        {/* Phone */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#F5F5F5] rounded-full flex items-center justify-center">
            <Phone className="w-5 h-5 text-[#5E5E5E]" />
          </div>
          <div>
            <p className="text-xs text-[#808080] uppercase tracking-wider">Teléfono</p>
            <a href={`tel:${vcard.phone}`} className="text-[#3C3C3C] font-medium hover:text-[#C5C51E]">
              {vcard.phone || '-'}
            </a>
          </div>
        </div>

        {/* Email */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#F5F5F5] rounded-full flex items-center justify-center">
            <Mail className="w-5 h-5 text-[#5E5E5E]" />
          </div>
          <div>
            <p className="text-xs text-[#808080] uppercase tracking-wider">Email</p>
            <a href={`mailto:${vcard.email}`} className="text-[#3C3C3C] font-medium hover:text-[#C5C51E]">
              {vcard.email || '-'}
            </a>
          </div>
        </div>

        {/* Company */}
        {vcard.company && (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#F5F5F5] rounded-full flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#5E5E5E]" />
            </div>
            <div>
              <p className="text-xs text-[#808080] uppercase tracking-wider">Empresa</p>
              <p className="text-[#3C3C3C] font-medium">{vcard.company}</p>
            </div>
          </div>
        )}

        {/* Address */}
        {(vcard.address || vcard.city || vcard.country) && (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#F5F5F5] rounded-full flex items-center justify-center">
              <MapPin className="w-5 h-5 text-[#5E5E5E]" />
            </div>
            <div>
              <p className="text-xs text-[#808080] uppercase tracking-wider">Dirección</p>
              <p className="text-[#3C3C3C] font-medium">
                {[vcard.address, vcard.city, vcard.country].filter(Boolean).join(', ')}
              </p>
            </div>
          </div>
        )}

        {/* Social Links */}
        {(social.website || social.linkedin || social.twitter || social.facebook || social.instagram) && (
          <div className="pt-4 border-t border-[#C3C3C3]">
            <p className="text-xs text-[#808080] uppercase tracking-wider mb-3">Redes Sociales</p>
            <div className="flex gap-3">
              {social.website && (
                <a 
                  href={social.website} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-[#F5F5F5] rounded-full flex items-center justify-center hover:bg-[#E7E723]/20 transition-colors"
                >
                  <Globe className="w-5 h-5 text-[#5E5E5E]" />
                </a>
              )}
              {social.linkedin && (
                <a 
                  href={social.linkedin} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-[#F5F5F5] rounded-full flex items-center justify-center hover:bg-[#E7E723]/20 transition-colors"
                >
                  <Linkedin className="w-5 h-5 text-[#5E5E5E]" />
                </a>
              )}
              {social.twitter && (
                <a 
                  href={social.twitter} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-[#F5F5F5] rounded-full flex items-center justify-center hover:bg-[#E7E723]/20 transition-colors"
                >
                  <Twitter className="w-5 h-5 text-[#5E5E5E]" />
                </a>
              )}
              {social.facebook && (
                <a 
                  href={social.facebook} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-[#F5F5F5] rounded-full flex items-center justify-center hover:bg-[#E7E723]/20 transition-colors"
                >
                  <Facebook className="w-5 h-5 text-[#5E5E5E]" />
                </a>
              )}
              {social.instagram && (
                <a 
                  href={social.instagram} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-[#F5F5F5] rounded-full flex items-center justify-center hover:bg-[#E7E723]/20 transition-colors"
                >
                  <Instagram className="w-5 h-5 text-[#5E5E5E]" />
                </a>
              )}
            </div>
          </div>
        )}

        {/* QR Code */}
        <div className="pt-4 border-t border-[#C3C3C3]">
          <p className="text-xs text-[#808080] uppercase tracking-wider mb-3 text-center">
            QR offline para guardar contacto
          </p>
          <div className="flex justify-center">
            <div className="bg-white p-4 rounded-sm border border-[#C3C3C3]">
              <QRCodeSVG 
                id={`qr-${vcard.id}`}
                value={qrPayload}
                size={qrSize}
                level={QR_ERROR_CORRECTION_LEVEL}
                boostLevel={false}
                includeMargin={true}
                className="max-w-full h-auto"
              />
            </div>
          </div>
          <p className="text-xs text-[#A2A2A2] text-center mt-2">
            vCard directa: {payloadMetrics.utf8_bytes} bytes, QR v{payloadMetrics.estimated_qr_version || '>'}40
          </p>
          {photoStatus.warning && (
            <p className="text-xs text-amber-700 text-center mt-2" data-testid="offline-photo-warning">
              {photoStatus.warning}
            </p>
          )}
        </div>

        {publicDownloadUrl && (
          <div className="pt-4 border-t border-[#C3C3C3]">
            <a
              href={publicDownloadUrl}
              className="inline-flex w-full items-center justify-center gap-2 rounded-sm bg-[#C5C51E] px-4 py-3 text-sm font-semibold text-black transition-colors hover:bg-[#A3A318]"
              data-testid="public-download-vcard-link"
            >
              <Download className="w-4 h-4" />
              Guardar contacto
            </a>
            <p className="text-xs text-[#808080] text-center mt-2">
              Descarga el contacto con fotografía en Android y iPhone
            </p>
          </div>
        )}

        {/* Actions */}
        {showActions && (
          <div className="pt-4 flex gap-2">
            <Button 
              onClick={handleDownloadVCard}
              className="flex-1 bg-[#3C3C3C] hover:bg-[#5E5E5E] text-white"
              data-testid="download-vcard-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Descargar
            </Button>
            <Button 
              onClick={handleDownloadQR}
              variant="outline"
              className="flex-1 border-[#A2A2A2]"
              data-testid="download-qr-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              QR
            </Button>
            <Button 
              onClick={handleShare}
              variant="outline"
              className="border-[#A2A2A2]"
              data-testid="share-btn"
              title="Compartir tarjeta web"
              aria-label="Compartir tarjeta web"
            >
              <Share2 className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VCardPreview;
